import React, { useState, useEffect } from 'react';
import './TeacherDashboard.css';
import DashboardHeader from '../components/DashboardHeader';
import TeacherNavigationBar from '../components/TeacherNavigationBar';
import { BookIcon, UsersIcon, CalendarIcon, BellIcon, ChartIcon, SchoolIcon } from '../components/Icons';
import { toSentenceCase } from '../utils/textUtils';

function TeacherDashboard() {
  const [stats, setStats] = useState({
    total_students: 0,
    total_classes: 0,
    notes_pending: 0,
    absences_today: 0,
    completion_rate: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherInfo, setTeacherInfo] = useState({
    nom: '',
    prenom: '',
    matiere: ''
  });
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTrimester, setSelectedTrimester] = useState('1');
  const [students, setStudents] = useState([]);
  const [notes, setNotes] = useState({});
  const [savingStatus, setSavingStatus] = useState('brouillon'); // brouillon, complet, soumis
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const classes = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale'];
  const subjects = [
    'Mathématiques', 'Français', 'Anglais', 'Physique-Chimie', 
    'SVT', 'Histoire-Géographie', 'EPS', 'Arts Plastiques'
  ];

  useEffect(() => {
    loadDashboardData();
    loadTeacherInfo();
  }, []);

  const loadTeacherInfo = () => {
    let prenom = localStorage.getItem('teacher_prenom') || '';
    let nom = localStorage.getItem('teacher_nom') || '';
    let matiere = localStorage.getItem('teacher_matiere') || '';
    
    const rawUserData = localStorage.getItem('userData');
    if (rawUserData) {
      try {
        const uData = JSON.parse(rawUserData);
        if (uData) {
          if (!prenom && uData.prenom) prenom = uData.prenom;
          if (!nom && uData.nom) nom = uData.nom;
          if (!matiere && (uData.matiere || uData.specialite)) matiere = uData.matiere || uData.specialite;
        }
      } catch (e) {}
    }
    
    if (!prenom && !nom) {
      const teacherName = localStorage.getItem('teacher_name') || localStorage.getItem('user_name') || '';
      if (teacherName) {
        const nameParts = teacherName.trim().split(' ');
        prenom = nameParts[0] || '';
        nom = nameParts.slice(1).join(' ') || '';
      }
    }

    if (!prenom && !nom) {
      prenom = 'Sophie';
      nom = 'Martin';
    }

    if (!matiere) {
      matiere = 'Mathématiques';
    }

    setTeacherInfo({
      nom,
      prenom,
      matiere
    });
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Charger les statistiques du professeur
      const teacherId = localStorage.getItem('teacher_id');
      const statsResponse = await fetch(`http://127.0.0.1:8000/api/v1/teacher/dashboard/statistiques?teacher_id=${teacherId}`);
      const statsData = await statsResponse.json();
      
      setStats({
        total_students: statsData.total_students || 0,
        total_classes: statsData.total_classes || 0,
        notes_pending: statsData.notes_pending || 0,
        absences_today: statsData.absences_today || 0,
        completion_rate: statsData.completion_rate || 0
      });

      // Pas d'activités, classes ou notifications simulées
      setRecentActivities([]);
      setUpcomingClasses([]);
      setNotifications([]);

      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      // En cas d'erreur, tout reste à 0
      setStats({
        total_students: 0,
        total_classes: 0,
        notes_pending: 0,
        absences_today: 0,
        completion_rate: 0
      });
      setRecentActivities([]);
      setUpcomingClasses([]);
      setNotifications([]);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      default: return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'info': return '#0078D7';
      case 'warning': return '#f39c12';
      case 'success': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const handleNoteChange = (studentId, field, value) => {
    setNotes(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
    setSavingStatus('brouillon');
  };

  const saveDraft = async () => {
    try {
      const notesData = Object.entries(notes).map(([studentId, noteData]) => ({
        etudiant_id: studentId,
        matiere: selectedSubject,
        trimestre: selectedTrimester,
        annee_scolaire: '2024-2025',
        interrogation1: noteData.interrogation1,
        interrogation2: noteData.interrogation2,
        devoir: noteData.devoir,
        examen: noteData.examen,
        appreciation: noteData.appreciation,
        statut: 'brouillon'
      }));

      const response = await fetch('http://127.0.0.1:8000/api/v1/admin/notes/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: notesData })
      });

      if (response.ok) {
        alert('Brouillon enregistré avec succès');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const submitNotes = async () => {
    try {
      const notesData = Object.entries(notes).map(([studentId, noteData]) => ({
        etudiant_id: studentId,
        matiere: selectedSubject,
        trimestre: selectedTrimester,
        annee_scolaire: '2024-2025',
        interrogation1: noteData.interrogation1,
        interrogation2: noteData.interrogation2,
        devoir: noteData.devoir,
        examen: noteData.examen,
        appreciation: noteData.appreciation,
        statut: 'soumis'
      }));

      const response = await fetch('http://127.0.0.1:8000/api/v1/admin/notes/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ notes: notesData })
      });

      if (response.ok) {
        setSavingStatus('soumis');
        setShowSubmitModal(false);
        alert('Notes soumises à l\'administration avec succès');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const calculateMoyenne = (studentId) => {
    const studentNotes = notes[studentId];
    if (!studentNotes) return 0;
    
    const values = [
      parseFloat(studentNotes.interrogation1) || 0,
      parseFloat(studentNotes.interrogation2) || 0,
      parseFloat(studentNotes.devoir) || 0,
      parseFloat(studentNotes.examen) || 0
    ].filter(n => n > 0);
    
    if (values.length === 0) return 0;
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);
  };

  const checkIfComplete = () => {
    return Object.values(notes).every(studentNotes => 
      studentNotes.interrogation1 && 
      studentNotes.interrogation2 && 
      studentNotes.devoir && 
      studentNotes.examen
    );
  };

  useEffect(() => {
    if (checkIfComplete() && savingStatus === 'brouillon') {
      setSavingStatus('complet');
    }
  }, [notes, savingStatus]);

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <TeacherNavigationBar 
          title={toSentenceCase("Tableau de bord professeur")} 
          showBack={false}
        />
        <div className="admin-container">
          <main className="teacher-main admin-content-full">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{toSentenceCase("Chargement du tableau de bord...")}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <TeacherNavigationBar 
        title="Tableau de Bord Professeur" 
        showBack={false}
      />
      <div className="admin-container">
        <main className="teacher-main admin-content-full">
          <div className="teacher-dashboard">
            
            {/* Bannière de bienvenue Enseignant - Épurée & Institutionnelle */}
            <section className="welcome-section">
              <div className="teacher-banner-card">
                <div className="teacher-banner-left">
                  <span className="teacher-portal-label">Portail Enseignant</span>
                  
                  <h1 className="teacher-banner-heading">
                    Bonjour <span className="teacher-banner-name">{teacherInfo.prenom} {teacherInfo.nom}</span>
                  </h1>

                  <p className="teacher-banner-role">
                    Professeur de <strong className="teacher-banner-subject">{teacherInfo.matiere || 'Mathématiques'}</strong>
                  </p>

                  <div className="teacher-banner-date">
                    <CalendarIcon size={15} color="#94a3b8" /> {new Date().toLocaleDateString('fr-FR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>

                <div className="teacher-banner-right">
                  <div className="teacher-avatar-badge">
                    {((teacherInfo.prenom?.[0] || 'P') + (teacherInfo.nom?.[0] || 'R')).toUpperCase()}
                  </div>
                </div>
              </div>
            </section>
            
            {/* Section 1: Message d'information si pas de classes */}
            {stats.total_classes === 0 && (
              <section className="info-section">
                <div className="info-card">
                  <div className="info-icon">
                    <BookIcon size={32} color="#0078D7" />
                  </div>
                  <div className="info-content">
                    <h3>Aucune classe assignée</h3>
                    <p>Vous n'avez pas encore de classes assignées. L'administration doit vous assigner des classes pour que vous puissiez :</p>
                    <ul className="info-list-clean">
                      <li>• Voir vos classes dans "Mes classes"</li>
                      <li>• Saisir les notes de vos élèves</li>
                      <li>• Gérer les absences</li>
                    </ul>
                    <div className="info-action">
                      <strong>Conseil :</strong>
                      <p>Contactez l'administration pour qu'elle vous assigne des classes depuis le menu "Attribution des classes" du dashboard admin.</p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Section 2: Accès Rapides Métier */}
            <section className="dashboard-section">
              <h2 className="section-title">Actions de Saisie & Évaluation</h2>
              <div className="quick-access-grid">
                <div 
                  className="quick-access-card"
                  style={{ borderTopColor: '#0078D7' }}
                  onClick={() => window.navigateTo && window.navigateTo('teacher-notes')}
                >
                  <div className="card-icon" style={{ backgroundColor: '#0078D7' }}>
                    <BookIcon size={32} color="#fff" />
                  </div>
                  <div className="card-content">
                    <p className="card-title">{toSentenceCase("Saisir les notes")}</p>
                    <h3 className="card-count">{stats.notes_pending} en attente</h3>
                  </div>
                </div>

                <div 
                  className="quick-access-card"
                  style={{ borderTopColor: '#FF6B35' }}
                  onClick={() => window.navigateTo && window.navigateTo('teacher-absences')}
                >
                  <div className="card-icon" style={{ backgroundColor: '#FF6B35' }}>
                    <CalendarIcon size={32} color="#fff" />
                  </div>
                  <div className="card-content">
                    <p className="card-title">{toSentenceCase("Gérer les absences")}</p>
                    <h3 className="card-count">{toSentenceCase("Faire l'appel")}</h3>
                  </div>
                </div>
              </div>
            </section>

            <section className="dashboard-section" style={{ marginTop: '1.5rem' }}>
              <h2 className="section-title">Consultation & Messagerie</h2>
              <div className="quick-access-grid">
                <div 
                  className="quick-access-card"
                  style={{ borderTopColor: '#9C27B0' }}
                  onClick={() => window.navigateTo && window.navigateTo('teacher-classes')}
                >
                  <div className="card-icon" style={{ backgroundColor: '#9C27B0' }}>
                    <SchoolIcon size={32} color="#fff" />
                  </div>
                  <div className="card-content">
                    <p className="card-title">{toSentenceCase("Mes classes")}</p>
                    <h3 className="card-count">{stats.total_classes} classe(s)</h3>
                  </div>
                </div>

                <div 
                  className="quick-access-card"
                  style={{ borderTopColor: '#DC3545' }}
                  onClick={() => window.navigateTo && window.navigateTo('teacher-messages')}
                >
                  <div className="card-icon" style={{ backgroundColor: '#DC3545' }}>
                    <BellIcon size={32} color="#fff" />
                  </div>
                  <div className="card-content">
                    <p className="card-title">{toSentenceCase("Messagerie & Annonces")}</p>
                    <h3 className="card-count">{toSentenceCase("Consulter")}</h3>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2: Statistiques */}
            <section className="dashboard-section">
              <h2 className="section-title">{toSentenceCase("Statistiques")}</h2>
              <div className="stats-grid">
                <div className="stat-card-simple">
                  <p className="stat-label">{toSentenceCase("Total étudiants")}</p>
                  <h3 className="stat-value">{stats.total_students}</h3>
                </div>
                <div className="stat-card-simple">
                  <p className="stat-label">{toSentenceCase("Total classes")}</p>
                  <h3 className="stat-value">{stats.total_classes}</h3>
                </div>
                <div className="stat-card-simple">
                  <p className="stat-label">{toSentenceCase("Notes en attente")}</p>
                  <h3 className="stat-value">{stats.notes_pending}</h3>
                </div>
                <div className="stat-card-simple">
                  <p className="stat-label">{toSentenceCase("Taux de complétion")}</p>
                  <h3 className="stat-value">{stats.completion_rate}%</h3>
                </div>
              </div>
            </section>

          </div>
        </main>
      </div>
    </div>
  );
}

export default TeacherDashboard;
